import { useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { List, Loader, Spacer } from 'theSource/components';
import { api, useApiQuery } from 'theSource/utils';
import {
  BenefitsPageResponseDto,
  ConfigurationResponseDto,
  GET_BENEFITS_QUERY_KEY,
  useAdminRedirectQuery,
  useCompanyManifestQuery,
  useCompanySettingsQuery,
  useEnrollmentContextQuery,
  useFeedMessagesQuery,
} from 'theSource/queries';
import { InboxCarousel, QuickLinkList, TileSet } from './components';
import { TileSetResponseDto } from './types';
import { GET_TILES_QUERY_KEY, mapTiles } from './utils';
import { selectIsAuthenticated } from 'theSource/features';
import { setUnreadCount } from 'theSource/features/Inbox/services';
import { UnreadMessageCountDto } from 'theSource/types';
import { selectIsAdmin } from 'theSource/features/AccessDenied';
import { ROUTES } from 'theSource/app/routes';
import { SUBSCRIBER_LOGIN_RESPONSE_CODE } from 'theSource/features/AccessDenied/utils';
import { EnrollmentBanner } from './components/EnrollmentBanner';
import {
  getCurrentContext,
  isLifeEventNotStarted,
} from './components/EnrollmentBanner/utils';
import { ToDoList } from 'enrollment/features/ToDoList';
import { setEnrollmentContext } from './components/EnrollmentBanner/services';
import { LifeEventBox } from 'enrollment/components/Shared';

interface HomeProps {
  configuration: ConfigurationResponseDto;
  lumosEnabled: boolean;
  sourceEnabled: boolean | undefined;
}

export const Home = ({
  configuration,
  lumosEnabled,
  sourceEnabled,
}: HomeProps) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated) || false;
  const isAdmin = useSelector(selectIsAdmin);
  const { admin } = useAdminRedirectQuery();

  const { feedMessages, isLoadingFeedMessages, isFetchingFeedMessages } =
    useFeedMessagesQuery({ enabled: sourceEnabled && !isAdmin });

  const { data: unreadCount, isLoading: isLoadingUnreadMessages } =
    useApiQuery<UnreadMessageCountDto>({
      queryKey: ['unreadFeedMessages'],
      endpoint: '/feed_messages/unread',
      showErrorSnackbar: false,
      enabled: sourceEnabled && !isAdmin,
    });

  let benefitsData: BenefitsPageResponseDto | undefined = undefined;
  if (sourceEnabled) {
    benefitsData = queryClient.getQueryData(GET_BENEFITS_QUERY_KEY)!;
  }

  const { data, isLoading: isLoadingTiles } = useQuery<TileSetResponseDto>(
    GET_TILES_QUERY_KEY,
    () => api.get<TileSetResponseDto>('/pwa/pwa_tiles'),
    { enabled: sourceEnabled }
  );

  const { data: manifestData } = useCompanyManifestQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost) && !isAdmin,
  });

  const {
    enrollmentContext,
    isLoadingEnrollmentContext,
    isFetchingEnrollmentContext,
  } = useEnrollmentContextQuery({
    enabled: !isAdmin && lumosEnabled && isAuthenticated,
    apiHost: configuration.apiHost,
  });

  const tiles =
    data &&
    mapTiles(data.data.pwa_tiles, data.data.evive_access, companySettings);

  const isLumosLifeEvent =
    companySettings?.data?.lumos_feature_toggle &&
    isLifeEventNotStarted(enrollmentContext);

  const isLoading =
    (isLoadingTiles && !tiles) ||
    isLoadingFeedMessages ||
    isFetchingFeedMessages ||
    isLoadingUnreadMessages ||
    isLoadingEnrollmentContext ||
    isFetchingEnrollmentContext;

  useEffect(() => {
    if (enrollmentContext) {
      dispatch(setEnrollmentContext(getCurrentContext(enrollmentContext)));
    }
  });

  // this shouldn't ever trigger in practice, but is here as a fallback
  // in case user manipulates URL manually or the react router has an issue
  useEffect(() => {
    if (companySettings && !companySettings.data.source_license) {
      navigate(ROUTES.enrollment.path);
    }
  }, [companySettings, navigate]);

  useEffect(() => {
    if (!isLoadingUnreadMessages) {
      dispatch(setUnreadCount(Number(unreadCount?.data)));
    }
  }, [isLoadingUnreadMessages, dispatch, unreadCount?.data]);

  useEffect(() => {
    if (isAdmin && admin?.data !== SUBSCRIBER_LOGIN_RESPONSE_CODE) {
      navigate(ROUTES.public.accessDenied.path);
    }
  }, [isAdmin, admin?.data, companySettings?.data, navigate]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box data-testid="home-page">
      {companySettings?.data?.lumos_feature_toggle && (
        <EnrollmentBanner
          enrollmentContext={enrollmentContext}
          orgName={String(manifestData?.organization_name)}
        />
      )}
      <Spacer height={6} />
      <InboxCarousel messages={feedMessages?.splice(0, 3)} />
      <Stack spacing={8}>
        {lumosEnabled ? null : (
          <Box sx={{ width: { lg: '150%' } }}>
            <List
              items={[
                {
                  name: 'pages.home:goToMyEnrollmentOptions',
                  path: configuration.enrollmentUrl,
                },
              ]}
              isCombined={false}
              isHomepage={true}
            />
          </Box>
        )}
        {lumosEnabled && <ToDoList benefitsData={benefitsData} />}
        <TileSet tiles={tiles!} />
        <QuickLinkList data={benefitsData as BenefitsPageResponseDto} />
        {/* {companySettings?.data?.lumos_feature_toggle &&
          isLifeEventContext(enrollmentContext) && (
            <EnrollmentBanner
              enrollmentContext={enrollmentContext}
              orgName={String(manifestData?.organization_name)}
            />
          )} */}
        {isLumosLifeEvent && <LifeEventBox />}
      </Stack>
    </Box>
  );
};
